import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, apiRequest } from "../../api/client";
import type { components } from "../../api/generated/schema";
import {
  ConfirmModal,
  EditorModal,
  ErrorText,
  LockedNotice
} from "../catalog/CatalogPages";
import { ContextHeader } from "../shared/project-context";
import { demoClearSummary } from "../projects/useDemoDataClear";
import { AssistantMultiSelect } from "../shared/AssistantMultiSelect";
import { CheckboxMultiSelect } from "../shared/CheckboxMultiSelect";
import { TeacherSearchSelect } from "../shared/TeacherSearchSelect";
import {
  type WeekPattern,
  weekPatternLabel,
  weekPatternOptions
} from "../shared/weekPatterns";
import type { PlanningSummary } from "./PlanningPage";
import {
  AvailableTimeSlotsField,
  availableTimeSummary,
  isAvailableTimeSelectionValid,
  normalizeAvailableTimeSelection,
  type AvailableTimeCell,
  type AvailableTimeCellReference,
  type AvailableSlotOption,
  type AvailableSlotReference,
  type TimePreference
} from "./AvailableTimeSlotsField";
import { CourseTimeRulesPanel } from "./CourseTimeRulesPanel";
import {
  buildTeacherWorkloadLabels,
  TeacherWorkloadPanel,
  type TeacherWorkloadSource
} from "./TeacherWorkloads";
import {
  planDemoMessage,
  usePlanDemoData
} from "./usePlanDemoData";
import "./plans.css";

interface PlanReference {
  id: string;
  label: string;
  active: boolean;
}

interface PlanItem {
  id: string;
  course: PlanReference;
  main_teacher: PlanReference;
  assistant_teachers: PlanReference[];
  school_class: PlanReference;
  weekly_lessons: number;
  week_pattern: WeekPattern;
  consecutive: boolean;
  allow_multiple_groups_per_day: boolean;
  room_instance: PlanReference | null;
  time_preference: TimePreference;
  group_sizes: number[];
  sort_order: number;
  valid: boolean;
  invalid_fields: string[];
  available_time_slots: AvailableSlotReference[] | null;
  available_time_cells: AvailableTimeCellReference[] | null;
  non_adjacent_course_hints: PlanReference[];
}

interface PlanList {
  items: PlanItem[];
  condition_revision: number;
  locked: boolean;
}

interface CourseItem {
  id: string;
  name: string;
}

interface TeacherItem {
  id: string;
  name: string;
  planned_lessons: number | null;
}

interface ClassItem {
  id: string;
  grade_label: string;
  class_name: string;
}

interface RoomInstance {
  id: string;
  display_name: string;
}

interface RoomItem {
  id: string;
  name: string;
  instances: RoomInstance[];
}

interface CatalogList<T> {
  items: T[];
}

interface PlanForm {
  courseId: string;
  courseIds: string[];
  mainTeacherId: string;
  assistantTeacherIds: string[];
  classId: string;
  classIds: string[];
  weeklyLessons: number;
  weekPattern: WeekPattern;
  consecutive: boolean;
  allowMultiple: boolean;
  roomInstanceId: string;
  timePreference: TimePreference;
  availableTimeCells: AvailableTimeCell[] | null;
}

interface TimeSlotList {
  items: AvailableSlotOption[];
  condition_revision: number;
  locked: boolean;
}

const emptyForm: PlanForm = {
  courseId: "",
  courseIds: [],
  mainTeacherId: "",
  assistantTeacherIds: [],
  classId: "",
  classIds: [],
  weeklyLessons: 1,
  weekPattern: "EVERY",
  consecutive: false,
  allowMultiple: false,
  roomInstanceId: "",
  timePreference: "ANY",
  availableTimeCells: null
};

interface ResetImpact {
  detailPlans: number;
  availabilityRules: number;
  nonAdjacentRules: number;
  adjacentRules: number;
  referenceTimetable: boolean;
}

interface CourseTimeRuleSummary {
  rules: Array<{ id: string }>;
  adjacent_rules: Array<{ id: string }>;
}

interface PlanBatchResponse {
  created_plan_ids: string[];
  skipped_duplicates: unknown[];
}

export function PlansPage({
  projectId,
  planning
}: {
  projectId: string;
  planning: PlanningSummary;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PlanItem | "new" | null>(null);
  const [deleting, setDeleting] = useState<PlanItem | null>(null);
  const [form, setForm] = useState<PlanForm>(emptyForm);
  const [resetImpact, setResetImpact] = useState<ResetImpact | null>(null);
  const [filterClass, setFilterClass] = useState("ALL");
  const [filterCourse, setFilterCourse] = useState("ALL");
  const [filterTeacher, setFilterTeacher] = useState("ALL");
  const [compiledNotice, setCompiledNotice] = useState(() => {
    const key = `planning-compiled:${projectId}`;
    const message = sessionStorage.getItem(key) ?? "";
    sessionStorage.removeItem(key);
    return message;
  });
  const plans = useQuery({
    queryKey: ["plans", projectId],
    queryFn: () => apiRequest<PlanList>(`/projects/${projectId}/plans`)
  });
  const courses = useQuery({
    queryKey: ["courses", projectId],
    queryFn: () =>
      apiRequest<CatalogList<CourseItem>>(`/projects/${projectId}/courses`)
  });
  const teachers = useQuery({
    queryKey: ["teachers", projectId],
    queryFn: () =>
      apiRequest<CatalogList<TeacherItem>>(`/projects/${projectId}/teachers`)
  });
  const classes = useQuery({
    queryKey: ["classes", projectId],
    queryFn: () =>
      apiRequest<CatalogList<ClassItem>>(`/projects/${projectId}/classes`)
  });
  const rooms = useQuery({
    queryKey: ["rooms", projectId],
    queryFn: () =>
      apiRequest<CatalogList<RoomItem>>(`/projects/${projectId}/room-types`)
  });
  const slots = useQuery({
    queryKey: ["time-slots", projectId],
    queryFn: () =>
      apiRequest<TimeSlotList>(`/projects/${projectId}/time-slots`)
  });
  const courseTimeRules = useQuery({
    queryKey: ["course-time-rules", projectId],
    queryFn: () =>
      apiRequest<CourseTimeRuleSummary>(
        `/projects/${projectId}/course-time-rules`
      )
  });
  const mutation = useMutation({
    mutationFn: async (action: "save" | "delete") => {
      if (!plans.data) return;
      if (action === "delete" && deleting) {
        return apiRequest(
          `/projects/${projectId}/plans/${deleting.id}?expected_condition_revision=${plans.data.condition_revision}`,
          { method: "DELETE" }
        );
      }
      if (!editing) return;
      const create = editing === "new";
      const path =
        create
          ? `/projects/${projectId}/plans/batch`
          : `/projects/${projectId}/plans/${editing.id}`;
      return apiRequest(path, {
        method: create ? "POST" : "PATCH",
        body: JSON.stringify(
          create
            ? {
                items: form.classIds.flatMap((classId) =>
                  form.courseIds.map((courseId) => ({
                    course_id: courseId,
                    main_teacher_id: form.mainTeacherId,
                    assistant_teacher_ids: form.assistantTeacherIds,
                    class_id: classId,
                    weekly_lessons: form.weeklyLessons,
                    week_pattern: form.weekPattern,
                    consecutive: form.consecutive,
                    allow_multiple_groups_per_day: form.allowMultiple,
                    room_instance_id: form.roomInstanceId || null,
                    time_preference: form.timePreference,
                    available_time_slot_ids: slotIdsFromCells(form.availableTimeCells),
                    available_time_cells: form.availableTimeCells
                  }))
                ),
                expected_condition_revision: plans.data.condition_revision
              }
            : {
                course_id: form.courseId,
                main_teacher_id: form.mainTeacherId,
                assistant_teacher_ids: form.assistantTeacherIds,
                class_id: form.classId,
                weekly_lessons: form.weeklyLessons,
                week_pattern: form.weekPattern,
                consecutive: form.consecutive,
                allow_multiple_groups_per_day: form.allowMultiple,
                room_instance_id: form.roomInstanceId || null,
                time_preference: form.timePreference,
                available_time_slot_ids: slotIdsFromCells(form.availableTimeCells),
                available_time_cells: form.availableTimeCells,
                expected_condition_revision: plans.data.condition_revision
              }
        )
      });
    },
    onSuccess: async (result) => {
      if (editing === "new" && result) {
        const response = result as PlanBatchResponse;
        setCompiledNotice(
          `批量新增 ${response.created_plan_ids.length} 条任课计划${
            response.skipped_duplicates.length
              ? `；${response.skipped_duplicates.length} 条重复项已跳过`
              : ""
          }`
        );
      }
      setEditing(null);
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ["plans", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["planning", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await queryClient.invalidateQueries({ queryKey: ["plans", projectId] });
      }
    }
  });
  const reset = useMutation({
    mutationFn: () =>
      apiRequest<components["schemas"]["ResetPlanningResponse"]>(
        `/projects/${projectId}/planning/reset`,
        {
          method: "POST",
          body: JSON.stringify({
            expected_condition_revision: planning.condition_revision,
            confirm_impacts: true
          })
        }
      ),
    onSuccess: async () => {
      setResetImpact(null);
      queryClient.removeQueries({
        queryKey: ["planning-draft", projectId],
        exact: true
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["planning", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["project", projectId] })
      ]);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await queryClient.invalidateQueries({ queryKey: ["planning", projectId] });
      }
    }
  });
  const planDemo = usePlanDemoData(
    projectId,
    planning.condition_revision,
    () => undefined,
    (result) => {
      sessionStorage.setItem(
        `planning-demo-applied:${projectId}`,
        planDemoMessage(result)
      );
    },
    (result) => {
      setCompiledNotice(
        result.counts.cleared > 0
          ? `已清理 ${result.counts.cleared} 条未编辑的任课计划模拟数据；人工改动、依赖项和来源不确定的数据均已保留。`
          : "没有可安全清理的任课计划模拟数据，现有数据未发生变化。"
      );
    }
  );

  function openNew(): void {
    mutation.reset();
    setForm(emptyForm);
    setEditing("new");
  }

  function openEdit(item: PlanItem): void {
    mutation.reset();
    const availableTime = normalizeAvailableTimeSelection(
      item.time_preference,
      item.available_time_cells,
      slots.data?.items ?? []
    );
    setForm({
      courseId: item.course.active ? item.course.id : "",
      courseIds: [item.course.id],
      mainTeacherId: item.main_teacher.active ? item.main_teacher.id : "",
      assistantTeacherIds: item.assistant_teachers.map((teacher) => teacher.id),
      classId: item.school_class.active ? item.school_class.id : "",
      classIds: [item.school_class.id],
      weeklyLessons: item.weekly_lessons,
      weekPattern: item.week_pattern,
      consecutive: item.consecutive,
      allowMultiple: item.allow_multiple_groups_per_day,
      roomInstanceId: item.room_instance?.active ? item.room_instance.id : "",
      timePreference: availableTime.timePreference,
      availableTimeCells: availableTime.availableTimeCells
    });
    setEditing(item);
  }

  const allRooms = (rooms.data?.items ?? []).flatMap((room) => room.instances);
  const loadingCatalog =
    courses.isLoading || teachers.isLoading || classes.isLoading || rooms.isLoading || slots.isLoading;
  const plansLocked = plans.data?.locked ?? false;
  const allPlans = plans.data?.items ?? [];
  const visiblePlans = allPlans.filter(
    (item) =>
      (filterClass === "ALL" || item.school_class.id === filterClass) &&
      (filterCourse === "ALL" || item.course.id === filterCourse) &&
      (filterTeacher === "ALL" ||
        item.main_teacher.id === filterTeacher ||
        item.assistant_teachers.some((teacher) => teacher.id === filterTeacher))
  );
  const teacherWorkloadSources: TeacherWorkloadSource[] = allPlans.map(
    (item) => ({
      mainTeacherId: item.main_teacher.id,
      assistantTeacherIds: item.assistant_teachers.map((teacher) => teacher.id),
      minimum: item.weekly_lessons,
      maximum: item.weekly_lessons,
      weekPattern: item.week_pattern
    })
  );
  const teacherItems = teachers.data?.items ?? [];
  const teacherWorkloadLabels = buildTeacherWorkloadLabels(
    teacherItems,
    teacherWorkloadSources
  );
  const teacherOptions = teacherItems.map((teacher) => ({
    ...teacher,
    workloadLabel: teacherWorkloadLabels.get(teacher.id)
  }));

  return (
    <main className="page-shell page-shell-wide">
      <ContextHeader
        projectId={projectId}
        eyebrow="任课计划 · 阶段 2/2"
        title="详细任课计划"
        backTo={{ href: `/projects/${projectId}`, label: "返回课表主页" }}
        actions={
          !planning.locked ? (
            <div className="header-actions">
              <button
                className="secondary-button danger-text"
                type="button"
                disabled={planDemo.clear.pending || planDemo.mutation.isPending}
                onClick={planDemo.clear.start}
              >
                {planDemo.clear.previewMutation.isPending
                  ? "正在统计…"
                  : "一键清除模拟数据"}
              </button>
              <button
                className="plan-demo-button"
                type="button"
                disabled={planDemo.mutation.isPending || planDemo.clear.pending}
                onClick={planDemo.start}
              >
                {planDemo.mutation.isPending ? "正在填入…" : "使用模拟数据"}
              </button>
              <button
                className="ghost-button danger-text"
                type="button"
                disabled={reset.isPending || planDemo.mutation.isPending}
                onClick={() => {
                  reset.reset();
                  setResetImpact({
                    detailPlans: planning.detail_plan_count,
                    availabilityRules: new Set(
                      allPlans
                        .filter((plan) => plan.available_time_slots !== null)
                        .map(
                          (plan) =>
                            `${plan.school_class.id}:${plan.course.id}`
                        )
                    ).size,
                    nonAdjacentRules: courseTimeRules.data?.rules.length ?? 0,
                    adjacentRules: courseTimeRules.data?.adjacent_rules.length ?? 0,
                    referenceTimetable: planning.has_reference_timetable
                  });
                }}
              >
                清空并重新编制
              </button>
            </div>
          ) : null
        }
      />
      {compiledNotice ? <p className="success-notice" role="status">{compiledNotice}</p> : null}
      {planDemo.clear.visibleError ? <ErrorText error={planDemo.clear.visibleError} /> : null}
      {!planning.locked && allPlans.length > 0 ? (
        <section className="plan-next-step" aria-label="下一步">
          <div>
            <span>下一步</span>
            <strong>详细任课计划已生成</strong>
          </div>
          <a
            className="solid-button plan-start-button"
            href={`/projects/${projectId}?action=schedule`}
          >
            开始预检
          </a>
        </section>
      ) : null}
      {plans.data?.locked ? <LockedNotice /> : null}
      {planning.locked ? (
        <p className="field-hint locked-recompile-hint">
          已排课项目不能原地重新编制；请从课表项目首页复制一份可编辑副本。
        </p>
      ) : null}
      <CourseTimeRulesPanel
        projectId={projectId}
        conditionRevision={plans.data?.condition_revision ?? planning.condition_revision}
        classCoursePairs={allPlans.map((item) => ({
          classId: item.school_class.id,
          courseId: item.course.id
        }))}
      />
      {!resetImpact && reset.isError ? <ErrorText error={reset.error} /> : null}
      {planDemo.visibleError ? <ErrorText error={planDemo.visibleError} /> : null}
      {plans.isLoading ? <p className="state-message">正在加载任课计划…</p> : null}
      {plans.isError ? <ErrorText error={plans.error} /> : null}
      {plans.data ? (
        <div className="plan-layout">
          <section className="content-card plans-grid" aria-label="任课计划列表">
            <div className="data-toolbar">
              <div className="toolbar-filters" aria-label="筛选任课计划">
                <label className="field">
                  <span>班级</span>
                  <select
                    value={filterClass}
                    onChange={(event) => setFilterClass(event.target.value)}
                  >
                    <option value="ALL">全部班级</option>
                    {(classes.data?.items ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.grade_label}{item.class_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>课程</span>
                  <select
                    value={filterCourse}
                    onChange={(event) => setFilterCourse(event.target.value)}
                  >
                    <option value="ALL">全部课程</option>
                    {(courses.data?.items ?? []).map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>教师</span>
                  <select
                    value={filterTeacher}
                    onChange={(event) => setFilterTeacher(event.target.value)}
                  >
                    <option value="ALL">全部教师</option>
                    {(teachers.data?.items ?? []).map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <span className="toolbar-count">
                {visiblePlans.length === allPlans.length
                  ? `共 ${allPlans.length} 条`
                  : `筛选出 ${visiblePlans.length} / ${allPlans.length} 条`}
              </span>
              <button
                className="solid-button"
                type="button"
                disabled={plansLocked || loadingCatalog}
                onClick={openNew}
              >
                批量添加计划
              </button>
            </div>
            {plans.data.items.length === 0 ? (
              <div className="empty-card compact">
                <h2>还没有任课计划</h2>
              </div>
            ) : null}
            {plans.data.items.length > 0 && visiblePlans.length === 0 ? (
              <p className="state-message">当前筛选下没有任课计划。</p>
            ) : null}
            {visiblePlans.length > 0 ? (
              <table className="data-table plans-table">
                <thead>
                  <tr>
                    <th>班级</th>
                    <th>课程</th>
                    <th>主教</th>
                    <th>助教</th>
                    <th>周课时</th>
                    <th>上课周次</th>
                    <th>连课/规则</th>
                    <th>可排节次</th>
                    <th>功能教室偏好</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePlans.map((item) => {
                    const selected =
                      editing !== null &&
                      editing !== "new" &&
                      editing.id === item.id;
                    return (
                      <tr
                        key={item.id}
                        className={`${selected ? "selected" : ""} ${item.valid ? "" : "planning-row-invalid"}`.trim()}
                        onClick={() => {
                          if (!plansLocked) openEdit(item);
                        }}
                      >
                        <td>{referenceText(item, "class_id", item.school_class)}</td>
                        <td>
                          {referenceText(item, "course_id", item.course)}{" "}
                          <span className={`pill ${item.valid ? "" : "warn"}`}>
                            {item.valid ? "可用于排课" : "待完善"}
                          </span>
                        </td>
                        <td>{referenceText(item, "main_teacher_id", item.main_teacher)}</td>
                        <td>{item.assistant_teachers.map((teacher) => teacher.label).join("、") || "无"}</td>
                        <td>{item.weekly_lessons} 节</td>
                        <td>{weekPatternLabel(item.week_pattern)}</td>
                        <td>
                          {item.consecutive ? "连课" : "不连课"}
                          {item.allow_multiple_groups_per_day ? " · 同日可多节" : ""}
                          {item.non_adjacent_course_hints.length > 0 ? (
                            <span>与{item.non_adjacent_course_hints.map((course) => course.label).join("、")}不相邻</span>
                          ) : null}
                        </td>
                        <td className={
                          item.invalid_fields.includes("time_preference") ||
                          item.invalid_fields.includes("available_time_slot_ids")
                            ? "invalid-reference"
                            : ""
                        }>
                          {availableTimeSummary(
                            item.time_preference,
                            item.available_time_slots,
                            slots.data?.items ?? [],
                            item.available_time_cells
                          )}
                          {item.invalid_fields.includes("time_preference") ||
                          item.invalid_fields.includes("available_time_slot_ids")
                            ? "（当前不可用）"
                            : ""}
                        </td>
                        <td>{item.room_instance?.label ?? "无"}</td>
                        <td>
                          <div className="row-actions">
                            <button
                              className="link-button"
                              type="button"
                              disabled={plansLocked}
                              onClick={(event) => {
                                event.stopPropagation();
                                openEdit(item);
                              }}
                            >
                              编辑
                            </button>
                            <button
                              className="link-button danger-text"
                              type="button"
                              disabled={plansLocked}
                              onClick={(event) => {
                                event.stopPropagation();
                                setDeleting(item);
                              }}
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}
          </section>
          <aside className="plan-aside">
            <h3>计划概览</h3>
            <div className="fact">
              <strong>{allPlans.length}</strong>
              <span>条详细任课计划</span>
            </div>
            <div className="fact">
              <strong>{allPlans.filter((item) => !item.valid).length}</strong>
              <span>条待完善</span>
            </div>
            <TeacherWorkloadPanel
              teachers={teachers.data?.items ?? []}
              sources={teacherWorkloadSources}
              selectedTeacherId={filterTeacher === "ALL" ? undefined : filterTeacher}
            />
          </aside>
        </div>
      ) : null}
      {editing ? (
        <EditorModal
          wide
          title={editing === "new" ? "批量添加任课计划" : "编辑任课计划"}
          onClose={() => {
            if (mutation.isPending) return;
            mutation.reset();
            setEditing(null);
          }}
          onSubmit={() => mutation.mutate("save")}
          pending={mutation.isPending}
          submitDisabled={
            !form.mainTeacherId ||
            (editing === "new" &&
              (form.courseIds.length === 0 || form.classIds.length === 0)) ||
            !isAvailableTimeSelectionValid(
              form.timePreference,
              form.availableTimeCells,
              slots.data?.items ?? []
            )
          }
          error={mutation.error}
        >
          {editing !== "new" ? (
            <p className="impact-summary">
              {editing.school_class.label} · {editing.course.label}
            </p>
          ) : null}
          <div className="plan-form-grid">
            {editing === "new" ? (
              <>
                <CheckboxMultiSelect
                  legend="课程"
                  value={form.courseIds}
                  options={(courses.data?.items ?? []).map((item) => ({
                    id: item.id,
                    label: item.name
                  }))}
                  onChange={(courseIds) => setForm({ ...form, courseIds })}
                />
                <CheckboxMultiSelect
                  legend="授课班级"
                  value={form.classIds}
                  options={(classes.data?.items ?? []).map((item) => ({
                    id: item.id,
                    label: `${item.grade_label}${item.class_name}`
                  }))}
                  onChange={(classIds) => setForm({ ...form, classIds })}
                />
              </>
            ) : (
              <>
                <label className="field">
                  <span>课程</span>
                  <select
                    required
                    value={form.courseId}
                    onChange={(event) => setForm({ ...form, courseId: event.target.value })}
                  >
                    <option value="">请选择</option>
                    {(courses.data?.items ?? []).map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                  <FieldError error={mutation.error} path="course_id" />
                </label>
                <label className="field">
                  <span>授课班级</span>
                  <select
                    required
                    value={form.classId}
                    onChange={(event) => setForm({ ...form, classId: event.target.value })}
                  >
                    <option value="">请选择</option>
                    {(classes.data?.items ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.grade_label}{item.class_name}
                      </option>
                    ))}
                  </select>
                  <FieldError error={mutation.error} path="class_id" />
                </label>
              </>
            )}
            <div className="field">
              <TeacherSearchSelect
                label="主教老师"
                value={form.mainTeacherId}
                options={teacherOptions}
                onChange={(mainTeacherId) => setForm({
                  ...form,
                  mainTeacherId,
                  assistantTeacherIds: form.assistantTeacherIds.filter(
                    (id) => id !== mainTeacherId
                  )
                })}
              />
              <FieldError error={mutation.error} path="main_teacher_id" />
            </div>
            <div className="field">
              <AssistantMultiSelect
                value={form.assistantTeacherIds}
                options={teacherOptions}
                disabledIds={form.mainTeacherId ? [form.mainTeacherId] : []}
                onChange={(assistantTeacherIds) => setForm({ ...form, assistantTeacherIds })}
              />
              <FieldError error={mutation.error} path="assistant_teacher_ids" />
            </div>
            <label className="field">
              <span>一周课时数</span>
              <input
                type="number"
                min={1}
                max={15}
                required
                value={form.weeklyLessons}
                onChange={(event) => {
                  const weeklyLessons = event.currentTarget.valueAsNumber;
                  if (!Number.isInteger(weeklyLessons)) return;
                  setForm({
                    ...form,
                    weeklyLessons,
                    consecutive: weeklyLessons === 1 ? false : form.consecutive
                  });
                }}
              />
            </label>
            <label className="field">
              <span>上课周次</span>
              <select
                value={form.weekPattern}
                onChange={(event) =>
                  setForm({
                    ...form,
                    weekPattern: event.target.value as WeekPattern
                  })
                }
              >
                {weekPatternOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>指定功能教室</span>
              <select
                value={form.roomInstanceId}
                onChange={(event) =>
                  setForm({ ...form, roomInstanceId: event.target.value })
                }
              >
                <option value="">无</option>
                {allRooms.map((item) => (
                  <option key={item.id} value={item.id}>{item.display_name}</option>
                ))}
              </select>
            </label>
          </div>
          {editing === "new" ? (
            <p className="impact-summary">
              将创建 {form.classIds.length * form.courseIds.length} 个班级×课程组合；重复项会跳过，其他项正常保存。
            </p>
          ) : null}
          <AvailableTimeSlotsField
            slots={slots.data?.items ?? []}
            timePreference={form.timePreference}
            value={form.availableTimeCells}
            currentReferences={
              editing === "new" ? [] : editing.available_time_cells ?? []
            }
            onChange={(timePreference, availableTimeCells) =>
              setForm({ ...form, timePreference, availableTimeCells })
            }
          />
          <FieldError error={mutation.error} path="time_preference" />
          <div className="plan-rule-options">
            <label className={`check-row ${form.weeklyLessons === 1 ? "disabled" : ""}`}>
              <input
                type="checkbox"
                checked={form.consecutive}
                disabled={form.weeklyLessons === 1}
                onChange={(event) =>
                  setForm({ ...form, consecutive: event.target.checked })
                }
              />
              <span>
                连课
                <small>每次连续安排 2 节；奇数课时会多安排 1 次单节。</small>
              </span>
            </label>
            <label className="check-row">
              <input
                type="checkbox"
                checked={form.allowMultiple}
                onChange={(event) =>
                  setForm({ ...form, allowMultiple: event.target.checked })
                }
              />
              <span>
                同一天可安排多节本课程
              </span>
            </label>
          </div>
        </EditorModal>
      ) : null}
      {deleting ? (
        <ConfirmModal
          title="删除任课计划"
          description={`删除“${deleting.course.label} · ${deleting.school_class.label}”计划后，下一次自动排课不会再安排这些课时；当前课表不受影响。`}
          dangerLabel="确认删除"
          onClose={() => setDeleting(null)}
          onConfirm={() => mutation.mutate("delete")}
          pending={mutation.isPending}
          error={mutation.error}
        />
      ) : null}
      {resetImpact ? (
        <ConfirmModal
          title="清空任课计划，重新编制"
          description={`此操作将清空 ${resetImpact.detailPlans} 条详细任课计划和 ${resetImpact.availabilityRules} 条可用节次设置，并回到空白的基础规则编制页。将保留 ${resetImpact.nonAdjacentRules} 条课程不相邻设置和 ${resetImpact.adjacentRules} 条课程相邻设置，以及课程、班级、教师、功能教室和节次等基础信息${resetImpact.referenceTimetable ? "，以及当前课表作为参考" : ""}；当前课表不会被覆盖，但会提示需重新排课。`}
          dangerLabel="确认清空并重新编制"
          onClose={() => setResetImpact(null)}
          onConfirm={() => reset.mutate()}
          pending={reset.isPending}
          error={reset.error}
        />
      ) : null}
      {planDemo.confirmOpen ? (
        <ConfirmModal
          title="使用模拟数据并重新编制？"
          description="这会清空现有任课计划和可用节次设置，并替换为一套完整模拟规则；课程不相邻设置、基础资料和现有课表都会保留。"
          dangerLabel="确认使用模拟数据"
          onClose={planDemo.closeConfirm}
          onConfirm={planDemo.confirm}
          pending={planDemo.mutation.isPending}
          error={planDemo.confirmError}
        />
      ) : null}
      {planDemo.clear.preview ? (
        <ConfirmModal
          title="清除任课计划模拟数据？"
          description={`${demoClearSummary(planDemo.clear.preview)}。只会清理来源明确、从未人工改动且未被当前课表或其他数据引用的任课计划模拟记录；现有课表和全部历史版本保持不变。`}
          dangerLabel="确认清理"
          onClose={planDemo.clear.close}
          onConfirm={planDemo.clear.confirm}
          pending={planDemo.clear.clearMutation.isPending}
          error={planDemo.clear.clearMutation.error}
        />
      ) : null}
    </main>
  );
}

function referenceText(
  plan: PlanItem,
  field: string,
  reference: PlanReference
): React.JSX.Element | string {
  if (!plan.invalid_fields.includes(field)) return reference.label;
  return (
    <span className="invalid-reference" title={`原对象：${reference.label}`}>
      请选择 <small>（{reference.label}已删除）</small>
    </span>
  );
}

function FieldError({
  error,
  path
}: {
  error: unknown;
  path: string;
}): React.JSX.Element | null {
  if (!(error instanceof ApiError)) return null;
  const fieldError = error.fieldErrors.find((item) => item.path === path);
  return fieldError ? <small className="field-error">{fieldError.message}</small> : null;
}

function slotIdsFromCells(cells: AvailableTimeCell[] | null): string[] | null {
  return cells === null
    ? null
    : [...new Set(cells.map((cell) => cell.time_slot_id))];
}
